import React from 'react';
import { useChat } from '../context/ChatContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Settings, Add, Logout, QuizRounded, MenuBookRounded, ScienceRounded } from '@mui/icons-material';
import LanguageToggle from './common/LanguageToggle';
import { listAssignedTopics, PdfExtractionSummary } from '../services/api';

interface ConversationSidebarProps {
  onConversationSelect?: () => void;
  onOpenFeedbackModal: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({ onConversationSelect, onOpenFeedbackModal }) => {
  const {
    conversations,
    currentConversationId,
    selectConversation,
    isLoadingConversations,
    setIsConfigViewActive,
    isConfigViewActive,
    handleUpdateConversationTitle,
    isUpdatingConversationTitle,
    handleCreateConversation,
  } = useChat();

  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isChatEndpoint = location.pathname === '/chat';

  // Assigned topics (PDFs the teacher has assigned to this student's class).
  const [assignedTopics, setAssignedTopics] = React.useState<PdfExtractionSummary[]>([]);
  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    listAssignedTopics()
      .then((rows) => {
        if (!cancelled) setAssignedTopics(rows);
      })
      .catch(() => {
        // Silent: the sidebar shouldn't break chat if the topics endpoint hiccups.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const [editingConversationId, setEditingConversationId] = React.useState<number | null>(null);
  const [editingTitle, setEditingTitle] = React.useState<string>('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Clicking the avatar reveals the logout button
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    selectConversation(null);
  };

  const displayName = user?.student?.first_name || user?.name || user?.username || '';
  const avatarInitial = (displayName || '?').charAt(0).toUpperCase();

  const handleDoubleClick = (conv: { id: number; title: string | null }) => {
    setEditingConversationId(conv.id);
    setEditingTitle(conv.title || 'Untitled Chat');
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setEditingTitle(e.target.value);

  const handleSaveEdit = async () => {
    if (editingConversationId === null || !editingTitle.trim()) {
      handleCancelEdit();
      return;
    }
    await handleUpdateConversationTitle(editingConversationId, editingTitle.trim());
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleCancelEdit = () => {
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleSaveEdit();
    else if (event.key === 'Escape') handleCancelEdit();
  };

  React.useEffect(() => {
    if (editingConversationId !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingConversationId]);

  const handleBrainConfigClick = () => {
    selectConversation(null);
    setIsConfigViewActive(true);
    onConversationSelect?.();
  };

  const handleConversationClick = (convId: number) => {
    selectConversation(convId);
    onConversationSelect?.();
  };

  const handleNewChatClick = async () => {
    // Eagerly create the conversation so the coach OPENS proactively — its greeting
    // leads with the class's most recent work/homework — with the syllabus picker
    // rendered beneath it (MessageList shows the picker until the first student
    // reply). The student can pick a curriculum/topic OR just reply and go with the
    // coach's flow. Navigate to /chat first so this also works from other routes.
    if (!isChatEndpoint) navigate('/chat');
    setIsConfigViewActive(false);
    onConversationSelect?.();
    await handleCreateConversation();
  };

  // Helper: a stable emoji per conversation based on its ID
  const getConversationEmoji = (id: number) => {
    const emojis = ['🚀', '🔍', '💡', '🌈', '🦄', '🌟', '🧠', '🔭', '📚', '🧩', '🔮', '🌍'];
    return emojis[id % emojis.length];
  };

  return (
    <div className="sidebar-main">
      {/* Profile — avatar circle reveals student details + logout */}
      {user && (
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center w-full">
            <button
              type="button"
              onClick={() => setIsProfileOpen((open) => !open)}
              className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-violet-600 text-white font-bold text-lg hover:bg-violet-700 transition-colors"
              aria-haspopup="true"
              aria-expanded={isProfileOpen}
              title="Account"
            >
              {avatarInitial}
            </button>
            <div className="ml-3 min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate">{displayName}</div>
              {user.student && (
                <div className="text-xs text-slate-500 truncate">
                  Grade {user.student.grade}
                  {user.student.section ? ` - ${user.student.section}` : ''} · Roll {user.student.roll_number}
                </div>
              )}
            </div>
          </div>

          {/* Coach language — always visible so the student can switch anytime */}
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-slate-500">Coach language</span>
            <LanguageToggle />
          </div>

          {isProfileOpen && (
            <div className="mt-3">
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:text-red-600 hover:bg-slate-100 transition-colors"
                aria-label="Logout"
              >
                <Logout fontSize="small" /> Logout
              </button>
            </div>
          )}
        </div>
      )}

      {/* New Chat */}
      <div className="sidebar-header">
        <button onClick={handleNewChatClick} className="new-chat-btn">
          <Add className="mr-2" /> New Adventure
        </button>
        <button
          onClick={() => navigate('/assessments')}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50"
        >
          <QuizRounded fontSize="small" /> My Assessments
        </button>
        <button
          onClick={() => navigate('/homework')}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50"
        >
          <MenuBookRounded fontSize="small" /> My Homework
        </button>
        <button
          onClick={() => navigate('/projects')}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50"
        >
          <ScienceRounded fontSize="small" /> My Projects
        </button>
      </div>

      {/* Conversation List */}
      <div className="conversation-list custom-scrollbar">
        <h2 className="conversation-list-header">Your Chats</h2>

        {isLoadingConversations ? (
          <div className="conversation-list-loading">Loading chats...</div>
        ) : conversations.length === 0 ? (
          <div className="conversation-list-empty">
            <div className="text-3xl mb-2">🔍</div>
            No chats yet.<br />Start a new adventure!
          </div>
        ) : (
          <ul className="space-y-3">
            {conversations.map((conv) => (
              <li key={conv.id} className="conversation-item">
                {editingConversationId === conv.id ? (
                  <div className="conversation-edit-container">
                    <input
                      ref={inputRef}
                      type="text"
                      value={editingTitle}
                      onChange={handleTitleChange}
                      onKeyDown={handleKeyDown}
                      className="conversation-edit-input"
                      disabled={isUpdatingConversationTitle}
                    />
                    <div className="mt-2 flex justify-end space-x-2">
                      <button
                        onClick={handleSaveEdit}
                        className="conversation-edit-btn-save"
                        disabled={isUpdatingConversationTitle || !editingTitle.trim()}
                      >
                        {isUpdatingConversationTitle ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="conversation-edit-btn-cancel"
                        disabled={isUpdatingConversationTitle}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleConversationClick(conv.id)}
                    onDoubleClick={() => handleDoubleClick(conv)}
                    className={conv.id === currentConversationId && !isConfigViewActive
                      ? 'conversation-btn-active'
                      : 'conversation-btn-inactive'}
                    title={conv.title || 'Untitled Chat'}
                  >
                    <div className="conversation-emoji">
                      <span>{getConversationEmoji(conv.id)}</span>
                    </div>
                    <div className="conversation-content">
                      <div className="conversation-title">{conv.title || 'Untitled Chat'}</div>
                      <span className="conversation-date">
                        {new Date(conv.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Brain Config - hidden on the /chat endpoint */}
      {!isChatEndpoint && (
        <div className="sidebar-section">
          <button
            onClick={handleBrainConfigClick}
            className={isConfigViewActive ? 'brain-config-btn-active' : 'brain-config-btn'}
          >
            <Settings className="mr-2" /> Brain Config
          </button>
        </div>
      )}

      {/* Class Topics — PDFs assigned by the teacher to this student's class */}
      {user && assignedTopics.length > 0 && (
        <div className="px-3 pb-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
            📚 Class Topics
          </div>
          <ul className="space-y-1">
            {assignedTopics.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/topic/${t.id}`)}
                  className="w-full text-left text-sm text-slate-700 hover:text-violet-700 truncate"
                  title={t.file_name}
                >
                  {t.file_name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Feedback */}
      {user && (
        <div className="user-info-section">
          <button onClick={onOpenFeedbackModal} className="new-chat-btn">
            Give Feedback
          </button>
        </div>
      )}
    </div>
  );
};

export default ConversationSidebar;
